/**
 * ActionRow — one drag-built action in a step's THEN clause.
 *
 * A single row models "send [Email + WhatsApp + SMS] to [Recipient A, B, C]"
 * — at save time {@link actionRowToSpecs} expands this into N ActionSpec
 * entries (one per selected recipient × channel valid for that recipient).
 * Each recipient is rendered as a removable chip; an "+ Add recipient"
 * picker offers the rest of the catalog.
 *
 * The recipient list comes from the parent's catalog payload, which itself
 * mirrors `EMAIL_RECIPIENTS` / `PHONE_RECIPIENTS`. Recipients that aren't
 * valid for the currently-selected channel set are filtered out of the
 * picker.
 */

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  describeRecipient,
  EMAIL_RECIPIENTS,
  PHONE_RECIPIENTS,
  type NotificationRecipient,
} from '@mana/shared';
import type { VisualActionRow } from './serialize.js';

export type ActionCatalogRecipient = {
  id: NotificationRecipient;
  label: string;
  description: string;
  requiresContactSetup: boolean;
};

export interface ActionRowProps {
  row: VisualActionRow;
  readOnly: boolean;
  /** Recipients valid for ANY of the channels currently selected. */
  recipients: ReadonlyArray<ActionCatalogRecipient>;
  onChange: (next: VisualActionRow) => void;
  onRemove: () => void;
}

export function ActionRow({
  row,
  readOnly,
  recipients,
  onChange,
  onRemove,
}: ActionRowProps) {
  // Look-up by id for chip rendering.
  const byId = useMemo(() => {
    const m = new Map<string, ActionCatalogRecipient>();
    for (const r of recipients) m.set(r.id, r);
    return m;
  }, [recipients]);

  // What's already on the row vs what's still pickable.
  const remaining = useMemo(
    () => recipients.filter((r) => !row.recipients.includes(r.id)),
    [recipients, row.recipients],
  );

  const [pickerOpen, setPickerOpen] = useState(false);

  // Channels currently on the row, in display order.
  const channelsArr: Array<'email' | 'whatsapp' | 'sms'> = [];
  if (row.channels.email) channelsArr.push('email');
  if (row.channels.whatsapp) channelsArr.push('whatsapp');
  if (row.channels.sms) channelsArr.push('sms');

  const toggleChannel = (channel: 'email' | 'whatsapp' | 'sms') => {
    if (readOnly) return;
    const nextChannels = { ...row.channels, [channel]: !row.channels[channel] };
    // After the toggle, prune any recipient that no longer has a supported
    // channel. Always keep at least one recipient — fall back to 'borrower'
    // so the row stays valid.
    const pruned = row.recipients.filter((r) => recipientStillValid(r, nextChannels));
    const nextRecipients = pruned.length > 0 ? pruned : ['borrower'];
    onChange({ ...row, channels: nextChannels, recipients: nextRecipients });
  };

  const addRecipient = (id: NotificationRecipient) => {
    if (readOnly) return;
    if (row.recipients.includes(id)) return;
    onChange({ ...row, recipients: [...row.recipients, id] });
    setPickerOpen(false);
  };

  const removeRecipient = (id: string) => {
    if (readOnly) return;
    // Never let the row go empty — keep the last one.
    if (row.recipients.length <= 1) return;
    onChange({ ...row, recipients: row.recipients.filter((r) => r !== id) });
  };

  const hasCustom = row.recipients.includes('custom');
  const anySetupNeeded = row.recipients.some(
    (id) => (byId.get(id) ?? describeRecipientFallback(id)).requiresContactSetup,
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-wrap items-start gap-3 text-xs">
        {/* Channels --------------------------------------------------------- */}
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Send via
          </legend>
          <div className="flex items-center gap-1.5">
            {(['email', 'whatsapp', 'sms'] as const).map((c) => (
              <label
                key={c}
                className={clsx(
                  'inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 transition-colors',
                  row.channels[c]
                    ? c === 'email'
                      ? 'bg-brand-navy-50 text-brand-navy-800 ring-brand-navy-300'
                      : c === 'whatsapp'
                        ? 'bg-emerald-50 text-emerald-800 ring-emerald-300'
                        : 'bg-amber-50 text-amber-800 ring-amber-300'
                    : 'bg-white text-slate-500 ring-slate-200 hover:ring-slate-300',
                )}
              >
                <input
                  type="checkbox"
                  checked={row.channels[c]}
                  disabled={readOnly}
                  onChange={() => toggleChannel(c)}
                  className="sr-only"
                />
                {channelLabel(c)}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Recipients (multi) ---------------------------------------------- */}
        <div className="flex flex-1 flex-col gap-0.5 min-w-[16rem]">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Send to
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {row.recipients.map((id) => {
              const info = byId.get(id) ?? describeRecipientFallback(id);
              const removable = row.recipients.length > 1 && !readOnly;
              return (
                <span
                  key={id}
                  title={info.description}
                  className={clsx(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1',
                    'bg-brand-cream-100 text-brand-navy-800 ring-brand-navy-200',
                  )}
                >
                  {info.label}
                  {removable && (
                    <button
                      type="button"
                      onClick={() => removeRecipient(id)}
                      title="Remove recipient"
                      className="-mr-0.5 rounded-full px-0.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                    >
                      ✕
                    </button>
                  )}
                </span>
              );
            })}

            {/* + Add recipient picker */}
            {!readOnly && remaining.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setPickerOpen((v) => !v)}
                  className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-500 hover:border-brand-navy-300 hover:text-brand-navy-700"
                >
                  + Add recipient
                </button>
                {pickerOpen && (
                  <div className="absolute z-10 mt-1 max-h-64 w-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                    {remaining.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => addRecipient(r.id)}
                        title={r.description}
                        className="flex w-full flex-col items-start gap-0.5 border-b border-slate-100 px-3 py-1.5 text-left last:border-b-0 hover:bg-brand-cream-100"
                      >
                        <span className="text-[12px] font-medium text-brand-navy-800">
                          {r.label}
                        </span>
                        {r.description && (
                          <span className="line-clamp-2 text-[10px] text-slate-500">
                            {r.description}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Template --------------------------------------------------------- */}
        <label className="flex flex-1 flex-col gap-0.5 min-w-[12rem]">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Template
          </span>
          <input
            type="text"
            value={row.templateKey}
            onChange={(e) => onChange({ ...row, templateKey: e.target.value })}
            placeholder="e.g. cd-ready"
            disabled={readOnly}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200 disabled:bg-slate-50"
          />
        </label>

        {/* Remove ------------------------------------------------------------ */}
        <button
          type="button"
          disabled={readOnly}
          onClick={onRemove}
          title="Remove action"
          className="self-end rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          ✕
        </button>
      </div>

      {/* Custom-to free-form field -------------------------------------------- */}
      {hasCustom && (
        <label className="mt-2 block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Custom email / phone
          </span>
          <input
            type="text"
            value={row.customTo ?? ''}
            placeholder={
              row.channels.email && !row.channels.whatsapp && !row.channels.sms
                ? 'team@example.com'
                : '+15555550100'
            }
            disabled={readOnly}
            onChange={(e) => onChange({ ...row, customTo: e.target.value })}
            className="mt-0.5 w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200 disabled:bg-slate-50"
          />
        </label>
      )}

      {/* Setup hint --------------------------------------------------------- */}
      {anySetupNeeded && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-[10px] text-amber-800 ring-1 ring-amber-200">
          Heads up — one or more recipients on this row need contact info
          captured on the Pipeline drawer before this action can deliver.
        </p>
      )}
      {channelsArr.length === 0 && (
        <p className="mt-2 rounded bg-rose-50 px-2 py-1 text-[10px] text-rose-700 ring-1 ring-rose-200">
          Pick at least one channel — this row won&apos;t be saved otherwise.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function channelLabel(c: 'email' | 'whatsapp' | 'sms'): string {
  switch (c) {
    case 'email':
      return 'Email';
    case 'whatsapp':
      return 'WhatsApp';
    case 'sms':
      return 'SMS';
  }
}

function recipientStillValid(
  recipient: string,
  channels: VisualActionRow['channels'],
): boolean {
  // A recipient is valid as long as one of the active channels supports it.
  const emailOk =
    channels.email && (EMAIL_RECIPIENTS as readonly string[]).includes(recipient);
  const phoneOk =
    (channels.whatsapp || channels.sms) &&
    (PHONE_RECIPIENTS as readonly string[]).includes(recipient);
  return emailOk || phoneOk;
}

function describeRecipientFallback(role: string): ActionCatalogRecipient {
  if (
    (EMAIL_RECIPIENTS as readonly string[]).includes(role) ||
    (PHONE_RECIPIENTS as readonly string[]).includes(role)
  ) {
    const d = describeRecipient(role as NotificationRecipient);
    return {
      id: role as NotificationRecipient,
      label: d.label,
      description: d.description,
      requiresContactSetup: d.requiresContactSetup,
    };
  }
  return {
    id: 'custom',
    label: role,
    description: '',
    requiresContactSetup: false,
  };
}
